'use client';

import React, { useState } from "react";
import { Box, Fab, Typography, Stack, IconButton, ClickAwayListener } from "@mui/material";
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';

const FloatContact = () => {
    const [isOpen, setIsOpen] = useState(false);
    const t = useTranslations('FloatContact');

    const toggleOpen = () => setIsOpen((prev) => !prev);
    const handleClose = () => setIsOpen(false);

    const buttonHoverStyle = {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    };

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3">
                {/* Popover Menu */}
                {
                    isOpen && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 90,
                                right: 0,
                                backgroundColor: '#fff',
                                borderRadius: '10px',
                                boxShadow: '0 10px 40px -10px rgba(0,0,0,0.15)',
                                border: '1px solid rgba(0,0,0,0.05)',
                                p: 3,
                                width: '320px',
                                zIndex: 51,
                                transformOrigin: 'bottom right',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close Button X */}
                            <IconButton
                                onClick={handleClose}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    left: 12,
                                    color: '#94a3b8',
                                    transition: 'all 0.2s',
                                    '&:hover': { color: '#F15859', transform: 'rotate(90deg)', bgcolor: 'rgba(225, 29, 72, 0.05)' }
                                }}
                                aria-label="Close contact menu"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>

                            <Stack spacing={2} sx={{ mt: 3 }}>
                                {/* LINE Button */}
                                <Box
                                    component="a"
                                    href="https://line.me/R/ti/p/%40722hncia"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        backgroundColor: '#06C755',
                                        color: '#fff',
                                        borderRadius: '10px',
                                        py: 1.5,
                                        px: 2.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': { ...buttonHoverStyle, backgroundColor: '#05b64d' },
                                        gap: 2,
                                        boxShadow: '0 4px 12px rgba(6, 199, 85, 0.2)'
                                    }}
                                >
                                    <ChatBubbleIcon sx={{ fontSize: 28 }} />
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '13px' }}>
                                            {t('line_request')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '13px' }}>
                                            {t('line_send_text')}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Phone Button */}
                                <Box
                                    component="a"
                                    href="tel:06-6123-1071"
                                    sx={{
                                        backgroundColor: '#132c4b',
                                        color: '#fff',
                                        borderRadius: '10px',
                                        py: 1.5,
                                        px: 2.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textDecoration: 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': { ...buttonHoverStyle, backgroundColor: '#0f233c' },
                                        position: 'relative',
                                        boxShadow: '0 4px 12px rgba(19, 44, 75, 0.15)'
                                    }}
                                >
                                    <PhoneIcon sx={{ position: 'absolute', left: 24, fontSize: 22 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>
                                        06-6123-1071
                                    </Typography>
                                </Box>

                                {/* Contact Form Button */}
                                <Box
                                    component={Link}
                                    href="/contact"
                                    sx={{
                                        backgroundColor: '#132c4b',
                                        color: '#fff',
                                        borderRadius: '10px',
                                        py: 1.5,
                                        px: 2.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textDecoration: 'none',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': { ...buttonHoverStyle, backgroundColor: '#0f233c' },
                                        position: 'relative',
                                        boxShadow: '0 4px 12px rgba(19, 44, 75, 0.15)'
                                    }}
                                >
                                    <ContactMailIcon sx={{ position: 'absolute', left: 24, fontSize: 22 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>
                                        {t('free_consultation')}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )
                }

                {/* Main Toggle Button */}
                <Fab
                    variant="extended"
                    onClick={toggleOpen}
                    sx={{
                        backgroundColor: '#F15859',
                        color: '#fff',
                        fontWeight: 800,
                        py: 3.5,
                        px: 3,
                        '&:hover': { backgroundColor: '#be123c', transform: 'translateY(-2px)' },
                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        boxShadow: '0 4px 20px rgba(225, 29, 72, 0.35)',
                        zIndex: 51,
                        borderRadius: '10px',
                    }}
                >
                    <Box component="span" sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <Box component="span" sx={{
                            display: 'flex',
                            transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}>
                            {isOpen ? <CloseIcon /> : <EmailIcon />}
                        </Box>
                        <Typography component="span" sx={{ fontWeight: 800 }}>
                            {isOpen ? t('close') : t('contact_us')}
                        </Typography>
                    </Box>
                </Fab>
            </Box>
        </ClickAwayListener>
    );
};

export default FloatContact;